import { useState } from 'react';
import { useI18n } from 'src/i18n/i18nContext';
import { giftCardAPI, promotionAPI } from 'src/services/api';
import { getErrorMessage } from 'src/utils/apiErrorHandler';

export interface AppliedGiftCard {
  code: string;
  balance: number;
}
export interface AppliedPromo {
  code: string;
}

type Props =
  | {
      type: 'giftCard';
      discount: AppliedGiftCard | null;
      setDiscount: React.Dispatch<React.SetStateAction<AppliedGiftCard | null>>;
    }
  | {
      type: 'promo';
      discount: AppliedPromo | null;
      setDiscount: React.Dispatch<React.SetStateAction<AppliedPromo | null>>;
      fetchDetails: (code?: string) => void;
    };

const DiscountCode: React.FC<Props> = (props) => {
  const { type, discount, setDiscount } = props;

  const { t } = useI18n();

  const [code, setCode] = useState<string>('');
  const [applying, setApplying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!code.trim()) {
      setError(t.checkout.enterGiftCardCode);
      return;
    }

    setApplying(true);
    setError(null);

    try {
      if (type === 'giftCard') {
        const response = await giftCardAPI.getBalance(code);
        const balance = response.data.balance;

        if (balance <= 0) {
          throw new Error(t.checkout.giftCardNoBalance);
        }

        setDiscount({ code, balance });
      } else if (type === 'promo') {
        const response = await promotionAPI.validate(code);
        if (!response) {
          throw new Error(t.checkout.invalidCode);
        }
        props.fetchDetails(code);

        setDiscount({ code });
      }

      setCode('');
    } catch (err: any) {
      setError(getErrorMessage(err, t, t.checkout.invalidCode));
    } finally {
      setApplying(false);
    }
  };

  const handleRemove = () => {
    setDiscount(null);
    type === 'promo' && props.fetchDetails();
  };

  return discount ? (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-green-600">
          {t[type].applied}: {discount.code}
        </span>
        <button onClick={handleRemove} className="text-sm text-red-600 hover:underline">
          {t.checkout.remove}
        </button>
      </div>
    </div>
  ) : (
    <div className="border-t pt-4">
      <h3 className="mb-2 font-semibold">{t[type].have}</h3>
      <div className="flex space-x-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t.checkout.enterCode}
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          disabled={applying}
        />
        <button
          onClick={handleApply}
          disabled={applying}
          className="rounded-md bg-gray-800 px-4 py-2 text-white transition-colors hover:bg-gray-900 disabled:opacity-50"
        >
          {applying ? t.common.loading : t.checkout.apply}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default DiscountCode;
